<script async setup>
import { ref, computed, watch } from "vue";

const props = defineProps({
    min: {
        type: Number,
        required: true,
    },
    max: {
        type: Number,
        required: true,
    },
    step: {
        type: Number,
        required: true,
    },
    modelValue: {
        type: Object,
        required: true,
    },
    currency: { 
        type: String,
        default: "€"
    },
});

const emit = defineEmits(["update:modelValue"]);

const currentMin = ref(Number(props.modelValue?.min_price ?? props.min));
const currentMax = ref(Number(props.modelValue?.max_price ?? props.max));

watch(
    () => props.modelValue,
    (value) => {
        currentMin.value = Number(value?.min_price ?? props.min);
        currentMax.value = Number(value?.max_price ?? props.max);
    },
    { deep: true }
);

const effectiveStep = computed(() => {
    return Number(props.step) > 0 ? Number(props.step) : 1;
});

const MIN_DELTA_PERCENT = 0.055;

const minDelta = computed(() => {
    const min = Number(props.min);
    const max = Number(props.max);
    const span = max - min;

    if (span <= 0) {
        return effectiveStep.value;
    }

    return Math.max(effectiveStep.value, Math.round(span * MIN_DELTA_PERCENT));
});

const snap = (value) => {
    const min = Number(props.min);
    const max = Number(props.max);
    const step = effectiveStep.value;

    let nextValue = Number(value);

    if (Number.isNaN(nextValue)) {
        nextValue = min;
    }

    nextValue = Math.min(Math.max(nextValue, min), max);

    const snapped = min + Math.round((nextValue - min) / step) * step;

    // Floating-Point-Fehler eliminieren
    const decimals = String(step).includes(".") ? String(step).split(".")[1].length : 2;
    return Math.min(Math.max(parseFloat(snapped.toFixed(decimals)), min), max);
};

const emitValue = () => {
    emit("update:modelValue", {
        min_price: currentMin.value,
        max_price: currentMax.value,
    });
};

const onMinInput = (event) => {
    const nextValue = snap(event.target.value);
    const maxAllowed = currentMax.value - minDelta.value;

    currentMin.value = Math.min(nextValue, maxAllowed);
    currentMin.value = snap(currentMin.value);

    if (currentMax.value - currentMin.value < minDelta.value) {
        currentMin.value = snap(currentMax.value - minDelta.value);
    }

    if (currentMin.value < props.min) {
        currentMin.value = props.min;
    }

    emitValue();
};

const onMaxInput = (event) => {
    const nextValue = snap(event.target.value);
    const minAllowed = currentMin.value + minDelta.value;

    currentMax.value = Math.max(nextValue, minAllowed);
    currentMax.value = snap(currentMax.value);

    if (currentMax.value - currentMin.value < minDelta.value) {
        currentMax.value = snap(currentMin.value + minDelta.value);
    }

    if (currentMax.value > props.max) {
        currentMax.value = props.max;
    }

    emitValue();
};

const minPos = computed(() => {
    const min = Number(props.min);
    const max = Number(props.max);

    if (max <= min) {
        return 0;
    }

    return ((currentMin.value - min) / (max - min)) * 100;
});

const maxPos = computed(() => {
    const min = Number(props.min);
    const max = Number(props.max);

    if (max <= min) {
        return 100;
    }

    return ((currentMax.value - min) / (max - min)) * 100;
});

const fmt = (val) => {
    const num = Number(val);
    return num % 1 === 0 ? num : parseFloat(num.toFixed(2));
};
</script>

<template>
    <div class="custom-slider">
        <div class="flex justify-between text-xs">
            <span>{{ props.min }}{{ props.currency }}</span>
            <span>{{ props.max }}{{ props.currency }}</span>
        </div>

        <div class="relative py-4">
            <div class="absolute inset-x-0 top-1/2 -translate-y-1/2 h-0.5 bg-gray-200 rounded-full"></div>

            <div
                class="absolute top-1/2 -translate-y-1/2 h-0.5 bg-black rounded-full"
                :style="{
                    left: `${minPos}%`,
                    width: `${maxPos - minPos}%`
                }"
            ></div>

            <input
                type="range"
                :min="props.min"
                :max="props.max"
                :step="effectiveStep"
                :value="currentMin"
                @input="onMinInput"
                class="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 w-full appearance-none bg-transparent z-20
                       [&::-webkit-slider-thumb]:pointer-events-auto
                       [&::-webkit-slider-thumb]:appearance-none
                       [&::-webkit-slider-thumb]:size-[14px]
                       [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-black
                       [&::-webkit-slider-thumb]:cursor-pointer
                       [&::-webkit-slider-thumb]:border-0
                       [&::-moz-range-thumb]:pointer-events-auto
                       [&::-moz-range-thumb]:appearance-none
                       [&::-moz-range-thumb]:size-[14px]
                       [&::-moz-range-thumb]:rounded-full
                       [&::-moz-range-thumb]:bg-black
                       [&::-moz-range-thumb]:cursor-pointer
                       [&::-moz-range-thumb]:border-0
                       [&::-webkit-slider-runnable-track]:bg-transparent
                       [&::-moz-range-track]:bg-transparent"
            >

            <input
                type="range"
                :min="props.min"
                :max="props.max"
                :step="effectiveStep"
                :value="currentMax"
                @input="onMaxInput"
                class="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 w-full appearance-none bg-transparent z-30
                       [&::-webkit-slider-thumb]:pointer-events-auto
                       [&::-webkit-slider-thumb]:appearance-none
                       [&::-webkit-slider-thumb]:size-[14px]
                       [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-black
                       [&::-webkit-slider-thumb]:cursor-pointer
                       [&::-webkit-slider-thumb]:border-0
                       [&::-moz-range-thumb]:pointer-events-auto
                       [&::-moz-range-thumb]:appearance-none
                       [&::-moz-range-thumb]:size-[14px]
                       [&::-moz-range-thumb]:rounded-full
                       [&::-moz-range-thumb]:bg-black
                       [&::-moz-range-thumb]:cursor-pointer
                       [&::-moz-range-thumb]:border-0
                       [&::-webkit-slider-runnable-track]:bg-transparent
                       [&::-moz-range-track]:bg-transparent"
            >
        </div>

        <div class="flex items-center justify-between text-xs">
            <span>{{ fmt(currentMin) }}{{ props.currency }}</span>
            <span>{{ fmt(currentMax) }}{{ props.currency }}</span>
        </div>
    </div>
</template>